// Minimal shadcn-style toast hook. Single global queue, FIFO, autodismiss after 5s.

import { useEffect, useState } from 'react';
import type { ToastActionElement } from './toast';

interface ToasterToast {
  id: string;
  title?: string;
  description?: string;
  action?: ToastActionElement;
  variant?: 'default' | 'destructive' | 'success';
}

type ToastInput = Omit<ToasterToast, 'id'>;

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY_MS = 5000;

let count = 0;
function genId(): string {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const listeners = new Set<(toasts: ToasterToast[]) => void>();
let queue: ToasterToast[] = [];

function emit(): void {
  for (const listener of listeners) listener(queue);
}

function dismiss(id: string): void {
  queue = queue.filter((t) => t.id !== id);
  emit();
}

export function toast(input: ToastInput): { id: string; dismiss: () => void } {
  const id = genId();
  const next: ToasterToast = { ...input, id };
  queue = [next, ...queue].slice(0, TOAST_LIMIT);
  emit();
  setTimeout(() => dismiss(id), TOAST_REMOVE_DELAY_MS);
  return { id, dismiss: () => dismiss(id) };
}

export function useToast(): { toasts: ToasterToast[]; dismiss: (id: string) => void } {
  const [toasts, setToasts] = useState<ToasterToast[]>(queue);
  useEffect(() => {
    listeners.add(setToasts);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);
  return { toasts, dismiss };
}
